/**
 * Company Logo component for job detail page
 */

import React from 'react';
import { getCompanyLogoSrcset, getCompanyLogoDefaultSrc } from '../../../../../utils/company/get-company-logo-url';
import { useLogoBackground } from '../../../../../hooks/useLogoBackground';

export interface CompanyLogoProps {
  companyId: string;
  companyURL?: string;
  disableLink?: boolean;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  isTyomarkkinatori?: boolean;
}

const SIZE_CLASSES = {
  small: 'w-10 h-10',
  medium: 'w-14 h-14',
  large: 'w-24 h-24',
  xlarge: 'w-32 h-32',
};

const IMAGE_SIZE_CLASSES = {
  small: 'w-8 h-8',
  medium: 'w-12 h-12',
  large: 'w-20 h-20',
  xlarge: 'w-28 h-28',
};

export const CompanyLogo: React.FC<CompanyLogoProps> = ({
  companyId,
  companyURL,
  disableLink = false,
  size = 'medium',
  isTyomarkkinatori: isTyomarkkinatoriProp,
}) => {
  const imgRef = React.useRef<HTMLImageElement | null>(null);
  
  // Use prop if provided, otherwise fallback to checking companyId (for backward compatibility)
  const isTyomarkkinatori = React.useMemo(() => {
    if (isTyomarkkinatoriProp !== undefined) {
      return isTyomarkkinatoriProp;
    }
    const normalized = companyId?.toLowerCase().trim();
    return normalized === 'työmarkkinatori' || normalized === 'tyomarkkinatori';
  }, [companyId, isTyomarkkinatoriProp]);
  
  // Generate srcset for WebP logos (64w, 128w, 200w)
  const srcset = React.useMemo(() => getCompanyLogoSrcset(companyId, isTyomarkkinatori), [companyId, isTyomarkkinatori]);
  const defaultSrc = React.useMemo(() => getCompanyLogoDefaultSrc(companyId, isTyomarkkinatori), [companyId, isTyomarkkinatori]);
  
  const defaultLogoSrc = '/company-logo-default.svg';

  // Initialize state with default values (important for SSR)
  const [currentSrc, setCurrentSrc] = React.useState<string>(defaultSrc);
  const [currentSrcSet, setCurrentSrcSet] = React.useState<string>(srcset);
  const hasTriedFallback = React.useRef(false);

  // Reset when company changes
  React.useEffect(() => {
    setCurrentSrc(defaultSrc);
    setCurrentSrcSet(srcset);
    hasTriedFallback.current = false;
  }, [companyId, defaultSrc, srcset]);

  // Also check if image failed to load after a timeout
  React.useEffect(() => {
    if (!srcset || hasTriedFallback.current) return;
    
    const timeout = setTimeout(() => {
      if (imgRef.current && !hasTriedFallback.current) {
        // Check if image is in error state (naturalWidth/Height are 0 and complete is true)
        const img = imgRef.current;
        if (img.complete && img.naturalWidth === 0 && img.naturalHeight === 0) {
          hasTriedFallback.current = true;
          img.srcset = '';
          img.src = defaultLogoSrc;
          setCurrentSrcSet('');
          setCurrentSrc(defaultLogoSrc);
        }
      }
    }, 2000); // Check after 2 seconds
    
    return () => clearTimeout(timeout);
  }, [srcset, defaultLogoSrc]);

  const handleError = React.useCallback(() => {
    // If WebP failed and we haven't tried the fallback yet, use default logo
    if (!hasTriedFallback.current && imgRef.current) {
      hasTriedFallback.current = true;
      const img = imgRef.current;
      // Clear srcset first, then set src to default logo
      img.srcset = '';
      img.src = defaultLogoSrc;
      setCurrentSrcSet('');
      setCurrentSrc(defaultLogoSrc);
    }
  }, [defaultLogoSrc]);

  // Detect if logo needs a background color
  const { backgroundColor, needsBackground } = useLogoBackground(
    currentSrc !== defaultLogoSrc ? currentSrc : undefined
  );

  // Get size in pixels for sizes attribute
  const sizePixels = size === 'small' ? 40 : size === 'medium' ? 56 : size === 'large' ? 96 : 128;

  // Determine background color - use detected color if needed, otherwise default
  const containerBackgroundColor = isTyomarkkinatori
    ? '#6B46C1' // Tyomarkkinatori purple
    : needsBackground && backgroundColor
    ? backgroundColor
    : '#fff'; // Default white

  // Determine container className - remove default background if we're applying a custom one
  const containerClassName = isTyomarkkinatori
    ? ''
    : needsBackground && backgroundColor
    ? 'border border-gray-200 shadow-sm'
    : 'bg-white border border-gray-200 shadow-sm';

  const logoImg = (
    <div
      className={`${SIZE_CLASSES[size]} flex items-center justify-center ${containerClassName} rounded-2xl overflow-hidden relative`}
      style={{
        borderRadius: '1.25rem',
        boxShadow: isTyomarkkinatori ? '0 2px 8px 0 rgba(107, 70, 193, 0.2)' : '0 2px 8px 0 rgba(31, 38, 135, 0.08)',
        backgroundColor: containerBackgroundColor,
        position: 'relative',
        padding: '0',
      }}
    >
      <img
        ref={imgRef}
        className={`object-contain w-full h-full`}
        alt={`${companyId} company logo`}
        src={currentSrc}
        srcSet={currentSrcSet || undefined}
        sizes={currentSrcSet ? `${sizePixels}px` : undefined}
        width={sizePixels}
        height={sizePixels}
        onError={handleError}
        loading="eager"
        decoding="async"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
          background: 'transparent',
          borderRadius: '1.25rem',
        }}
        onAbort={() => {
          // Handle image load abort
          handleError();
        }}
      />
    </div>
  );

  return companyURL && !disableLink ? (
    <a
      href={companyURL}
      target="_blank"
      rel="noopener noreferrer"
      title={companyId}
      style={{ display: 'inline-block' }}
      onClick={(e) => e.stopPropagation()}
    >
      {logoImg}
    </a>
  ) : (
    logoImg
  );
};

